import React, { useState, useCallback, useEffect } from 'react';
import { getFileUploadKey } from 'common/utilFunctions';
import { DocumentUploadDropzone } from 'components/organisms';
import ActionButtons from './ActionButtons';
import { updateShop } from '../../../graphql/mutations';
import { Storage, graphqlOperation, API } from 'aws-amplify';
import { useAmplifyAuth } from '../../../context';
import { Paper } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useShops } from 'context/shop/shopsContext';
import { useLoader } from 'layouts/loaderContext';

function ShopDocuments({
  hideUploader,
  setSelectedShop,
  setDisabled = () => {}
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { setLoading } = useLoader();
  const { dispatch } = useShops();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { user }
  } = useAmplifyAuth();

  useEffect(() => {
    setDisabled(!selectedFiles.length);
  }, [selectedFiles]);

  const onDrop = useCallback(
    (acceptedFiles) =>
      setSelectedFiles([
        ...selectedFiles,
        ...acceptedFiles.map((file) => ({
          file,
          name: '',
          documentType: '',
          description: ''
        }))
      ]),
    [selectedFiles]
  );

  const handleChange = (id) => (e) =>
    setSelectedFiles(
      selectedFiles.map((file, index) =>
        index === id ? { ...file, [e.target.name]: e.target.value } : file
      )
    );

  const onFileClose = (id) =>
    setSelectedFiles(selectedFiles.filter((item, index) => index !== id));

  const onCancel = () => setSelectedFiles([]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (
      selectedFiles.length === 0 ||
      selectedFiles.some((item) => !(item.name && item.documentType))
    ) {
      return enqueueSnackbar('All fields are mandatory', {
        variant: 'warning',
        preventDuplicate: true
      });
    }
    Storage.configure({ level: 'protected' });
    setLoading(true);
    const allUrls = [];
    const uploadedFiles = selectedFiles.map(
      async ({ file, name, documentType, description }) => {
        const { type: mimeType } = file;
        const key = getFileUploadKey(file, 'documents');
        return Storage.put(key, file, {
          contentType: mimeType
        })
          .then(() => ({
            name,
            documentType,
            description,
            documentURL: key,
            MIMETypes: mimeType
          }))
          .catch((error) => {
            console.log('error', error);
            return null;
          });
      }
    );
    try {
      const results = await Promise.allSettled(uploadedFiles);
      results.forEach(
        (item) => item.status === 'fulfilled' && allUrls.push(item.value)
      );
      const data = await API.graphql(
        graphqlOperation(updateShop, {
          input: {
            documents: allUrls,
            id: user.shopId
          }
        })
      );
      if (data) {
        enqueueSnackbar('Documents updated successfully', {
          variant: 'success',
          preventDuplicate: true
        });
        dispatch({
          type: 'updateShop',
          payload: data.data.updateShop
        });
        setSelectedShop(data.data.updateShop);
      }
      setDisabled(true);
      hideUploader();
    } catch (e) {
      console.log(e);
      enqueueSnackbar('Something went wrong...', {
        variant: 'error',
        preventDuplicate: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper style={{ boxShadow: 'none' }}>
      <DocumentUploadDropzone
        onDrop={onDrop}
        selectedFiles={selectedFiles}
        onClose={onFileClose}
        handleChange={handleChange}
      />
      <ActionButtons
        disabled={!selectedFiles.length}
        handleSubmit={onSubmit}
        handleCancel={onCancel}
      />
    </Paper>
  );
}

export default ShopDocuments;
